import { orderBy } from "lodash-es";
import { useNavigate } from "react-router";

import type { CommunityGroupV2Dto } from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { EntityCard } from "@/components/EntityCard/EntityCard";
import { Award01Icon } from "@/components/Icons/Award01Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Power01Icon } from "@/components/Icons/Power01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { UsersCheckIcon } from "@/components/Icons/UsersCheckIcon";
import { useShareEntityMenuItems } from "@/components/ShareButton/helpers";
import { ShareToChatModal } from "@/components/ShareToChatModal/ShareToChatModal";
import { ShareToCommunityFeedModal } from "@/components/ShareToCommunityFeedModal/ShareToCommunityFeedModal";
import { UserAvatarList } from "@/components/UserAvatarList/UserAvatarList";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { ToggleButton } from "@/design-system/ToggleButton";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { canClone } from "@/modules/community-groups/permissions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface GroupCardProps {
  group: CommunityGroupV2Dto;
  followButtonLabel: string;
  unfollowButtonLabel: string;
  onFollow: (id: string) => void;
  onUnfollow: (id: string) => void;
  onEnableGroup: (id: string) => void;
  onDisableGroup: (id: string) => void;
  onDeleteGroup: (id: string) => void;
}
export function GroupCard({
  group,
  followButtonLabel,
  unfollowButtonLabel,
  onFollow,
  onUnfollow,
  onEnableGroup,
  onDisableGroup,
  onDeleteGroup,
}: GroupCardProps): React.ReactNode {
  const [isShareToChatModalOpened, shareToChatModalHandler] = useBool(false);
  const [isShareToCommunityFeedModalOpened, shareToCommunityFeedModalHandler] = useBool(false);

  const { t } = useTranslation();
  const user = useSessionUser();
  const slug = useSlug();
  const hasPermission = usePermission();
  const navigate = useNavigate();
  const shareEntityMenuItems = useShareEntityMenuItems({
    deepLinkPayload: {
      entityType: "group",
      entityId: group.id,
      groupTypeId: group.type === "interest" ? "1" : "2",
    },
    callbacks: {
      shareToCommunityFeed: shareToCommunityFeedModalHandler.setTrue,
      shareToChat: shareToChatModalHandler.setTrue,
    },
  });

  const handleToggleFollowGroup = () => {
    if (group.isMember) onUnfollow(group.id);
    else onFollow(group.id);
  };

  const getGroupType = (): string => {
    if (group.type === "realEstate") {
      return "realEstate";
    }
    if (group.projectConnectionGroupId) {
      return "projectConnection";
    }
    if (group.systemGroupId) {
      return "system";
    }

    return "regular";
  };

  let editHref: string;
  if (group.isResidentGroup) {
    editHref = routes.interestGroups.editResidentGroup({ slug, id: group.id });
  } else if (group.type === "helpCategory") {
    editHref = routes.helpCategories.edit({ slug, id: group.id });
  } else {
    editHref = routes.interestGroups.edit({ slug, id: group.id });
  }

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      type: "link",
      isHidden: !group.canEdit,
      href: editHref,
    },
    {
      labels: {
        default: t("common.action.duplicate"),
      },
      value: "duplicate",
      icon: <Copy01Icon />,
      "data-testid": "group-list-duplicate-item",
      type: "button",
      isHidden:
        !hasPermission(canClone) ||
        !!group.systemGroupId ||
        !!group.projectConnectionGroupId ||
        !!group.isResidentGroup,
      callbackFn: () => {
        const duplicateRoute =
          group.type === "helpCategory" ? routes.helpCategories.create : routes.interestGroups.create;
        void navigate(
          duplicateRoute({
            slug,
          }),
          {
            state: {
              groupId: group.id,
            },
          },
        );
      },
    },
    {
      labels: {
        default: t("common.action.enable"),
      },
      value: "enable",
      icon: <Power01Icon />,
      "data-testid": "group-list-item-enable",
      type: "button",
      isHidden: !group.canDelete || !(group.systemGroupId || group.projectConnectionGroupId) || !group.disabled,
      callbackFn: () => onEnableGroup(group.id),
    },
    {
      labels: {
        default: t("common.action.disable"),
      },
      value: "disable",
      icon: <Trash02Icon />,
      "data-testid": "group-list-item-disable",
      type: "button",
      isHidden: !group.canDelete || (!group.systemGroupId && !group.projectConnectionGroupId) || group.disabled,
      callbackFn: () => onDisableGroup(group.id),
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      "data-testid": "context-menu-delete-btn",
      type: "button",
      isHidden: !group.canDelete || !!group.systemGroupId || !!group.projectConnectionGroupId,
      callbackFn: () => onDeleteGroup(group.id),
    },
    ...shareEntityMenuItems,
  ];
  const groupType = getGroupType();
  const detailLink =
    group.type === "helpCategory"
      ? routes.helpCategories.details({
          slug,
          id: group.id,
        })
      : routes.interestGroups.details({
          slug,
          id: group.id,
        });

  return (
    <>
      <EntityCard
        data-testid="group-list-item"
        href={detailLink}
        title={group.name}
        subtitle={group.description}
        bannerImg={group.image || user.project.backgroundImage}
        menuActions={menuActions}
        renderBadges={() => (
          <>
            {group.disabled && <Badge variant="danger">{t("common.label.disabled")}</Badge>}
            {user.isAdmin && group.hasAudience && <Badge icon={<UsersCheckIcon />} />}
            {user.isAdmin && groupType !== "regular" && (
              <Badge>
                {groupType === "realEstate" && t("page.discovery.group-type.real-estate")}
                {groupType === "projectConnection" && t("page.discovery.group-type.project-connection")}
                {groupType === "system" && t("page.discovery.group-type.platform")}
              </Badge>
            )}
          </>
        )}
        renderLeftAction={
          group.disabled
            ? undefined
            : () => (
                <UserAvatarList
                  images={orderBy(group.members.items, (x) => !!x.avatar).map((x) => x.avatar)}
                  limit={3}
                  totalElements={group.members.total}
                />
              )
        }
        renderCtaAction={() =>
          group.isOwner && group.isResidentGroup ? (
            <Button icon={<Award01Icon />} disabled={true}>
              {t("model.interest-group.owner")}
            </Button>
          ) : (
            <ToggleButton
              fullWidth="untilSm"
              data-testid="group-action-follow-unfollow"
              config={{
                checked: {
                  label: unfollowButtonLabel,
                  icon: <CheckIcon />,
                },
                unchecked: {
                  label: followButtonLabel,
                },
              }}
              size="sm"
              isChecked={group.isMember}
              onClick={(e) => {
                e.preventDefault();
                handleToggleFollowGroup();
              }}
            />
          )
        }
      />
      <ShareToCommunityFeedModal
        isOpened={isShareToCommunityFeedModalOpened}
        onOpenChange={shareToCommunityFeedModalHandler.set}
        entityId={group.id}
        entityType="group"
        otherIds={{
          groupTypeId: group.type === "interest" ? "1" : "2",
        }}
      />
      <ShareToChatModal
        isOpened={isShareToChatModalOpened}
        onOpenChange={shareToChatModalHandler.set}
        entityId={group.id}
        entityType="group"
        otherIds={{
          groupTypeId: group.type === "interest" ? "1" : "2",
        }}
      />
    </>
  );
}
